/**
 * What this tenant's plan carries — read once, shared by every gated surface.
 *
 * The server refuses an out-of-plan action with a 402 carrying the feature and
 * the cheapest plan that would allow it. That is correct, and it is also the
 * worst moment for an operator to learn it: they have already decided to do the
 * thing. This hook lets a control say so up front, in the server's own words —
 * the label and the upgrade target both come from the same registry the gate
 * reads, so the console and the refusal can never disagree.
 *
 * FAIL OPEN, DELIBERATELY, in three cases: the request failed, the response was
 * not ok, or the server told us it could not read the plan. `src/billing/gate.py`
 * lets a tenant through when the plan lookup fails, and a console that painted
 * locks the server is not enforcing would refuse work the API would have
 * allowed. `known` says which of the two states we are in, for a surface that
 * wants to wait rather than guess. `failed` narrows that further -- true only
 * once a read has actually failed or come back unreadable, so a surface that
 * wants to offer Retry (rather than just wait) can tell "still loading" apart
 * from "gave up" (F1 fix round 1, C2).
 */
import { useEffect, useState } from "react";

import {
  billingApi,
  type EntitlementFeature,
  type EntitlementsResponse,
  type EntitlementsServices,
} from "../shared/api.ts";

export interface PlanLock {
  /** Operator-facing feature name, e.g. "SMS automation". */
  label: string;
  /** Plan key that includes it, e.g. "growth". Null if nothing sold carries it. */
  includedFrom: string | null;
  /** Ready-to-render sentence: "SMS automation is included from Growth." */
  message: string;
}

const PLAN_LABELS: Record<string, string> = {
  trial: "Trial",
  starter: "Starter",
  growth: "Growth",
  scale: "Scale",
  enterprise: "Enterprise",
  founder: "Founder",
};

export interface Entitlements {
  /** False until the first answer arrives. */
  known: boolean;
  /** True once a read has failed or come back unreadable -- distinct from
   *  `known` staying false while a read is still simply in flight. Reset to
   *  false the moment a new read starts (mount, or `refreshEntitlements()`). */
  failed: boolean;
  plan: string | null;
  limits: EntitlementsResponse["limits"] | null;
  /** True when the plan carries `feature`, and true whenever we do not know. */
  has: (feature: string) => boolean;
  /** A lock to render, or null when the feature is available (or unknown). */
  lockFor: (feature: string) => PlanLock | null;
  /** Which of website support / the phone receptionist this tenant has, for
   *  `navServices.visibleNavPaths`. Null until known, and whenever the plan
   *  could not be read -- both cases mean "show every nav destination". */
  services: EntitlementsServices | null;
}

const OPEN: Entitlements = {
  known: false,
  failed: false,
  plan: null,
  limits: null,
  has: () => true,
  lockFor: () => null,
  services: null,
};

const FAILED: Entitlements = { ...OPEN, failed: true };

/** One in-flight `GET /api/billing/entitlements`, shared by everyone who asks
 *  during it.
 *
 *  `TeamPanel` and `ChannelsPanel` both mount on the Settings page and both
 *  call `useEntitlements()`, and StrictMode double-invokes each of their
 *  effects: four identical reads of one tenant's plan on every visit, three of
 *  them thrown away.
 *
 *  Same shape and the same reasons as `sharedAssistantConfig()` in
 *  appearance-context. This is a COALESCER, not a cache: the promise is dropped
 *  the moment it settles, so the next caller after that gets a fresh read, and
 *  there is no invalidation rule to get wrong. A cache here would show somebody
 *  another tenant's plan, which is worse than a second request.
 *
 *  It deliberately takes no `AbortSignal`: one hook's unmount must not cancel a
 *  read another hook is still waiting on. Staleness stays each hook's own
 *  business -- `useEntitlements` keeps its per-hook controller and checks
 *  `.aborted` after the await.
 */
let inFlightEntitlements: Promise<EntitlementsResponse> | null = null;

export function sharedEntitlements(): Promise<EntitlementsResponse> {
  if (inFlightEntitlements === null) {
    const request = billingApi.entitlements();
    inFlightEntitlements = request;
    const release = () => {
      // Only clear OUR request: a later caller may already have installed the
      // next one by the time this settles.
      if (inFlightEntitlements === request) inFlightEntitlements = null;
    };
    request.then(release, release);
  }
  return inFlightEntitlements;
}

/** Drops the shared read, so the next caller starts a new one.
 *
 *  The slot has no tenant key and this is a single page: a session change is a
 *  React state change, not a document load, so an in-flight GET outlives it.
 *  Without this, a tenant signing in while the previous tenant's read is still
 *  stalled would join that request and be shown the previous tenant's plan and
 *  seat count -- and a gated control would unlock or lock on the wrong plan.
 *
 *  Called from `setSession` in auth-context, beside
 *  `resetSharedAssistantConfig()`, which every session change goes through:
 *  sign-in, the callback, sign-out, expiry and the `authApi.me` identity
 *  mismatch. Not from a hook's cleanup -- an unmount is not a tenant change.
 *
 *  Unlike `refreshEntitlements()` below, this does NOT notify already-mounted
 *  hooks: a session change remounts the protected tree (AuthProvider swaps
 *  `session`), so there is nothing stale left mounted to tell.
 */
export function resetSharedEntitlements(): void {
  inFlightEntitlements = null;
}

/** Every mounted `useEntitlements()` instance's own "please re-read" trigger.
 *  A plain `Set` of setter-style callbacks, not a full pub/sub library --
 *  there is exactly one event ("re-read now") and no payload to deliver. */
const listeners = new Set<() => void>();

/**
 * Raw subscribe primitive behind `useEntitlements()`, exported for the one
 * caller that reads the shared entitlements read WITHOUT the hook:
 * `AccountCard` (SettingsPage) also needs `retention_days`, which is not part
 * of the `Entitlements` shape this hook returns, so it calls
 * `sharedEntitlements()` directly and keeps its own state. Without this it
 * would go stale on a refresh `useEntitlements()` callers pick up automatically.
 *
 * Returns an unsubscribe function, exactly like `useEffect`'s own cleanup
 * convention, so a caller can `useEffect(() => subscribeEntitlementsRefresh(fn), [fn])`.
 */
export function subscribeEntitlementsRefresh(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Forces every mounted `useEntitlements()` hook -- including ones mounted
 * long before this call, like `AppShell`'s sidebar and `CommandPalette` --
 * to read the plan again (F1 fix round 1, C1).
 *
 * `resetSharedEntitlements()` alone never did this: it only nulls an
 * already-null slot when nothing is in flight, and nulling the slot does not
 * make an already-settled `useState` re-render. This is the missing half --
 * a real broadcast, not a cache invalidation with nothing to invalidate.
 *
 * Drops the in-flight slot FIRST, so this is never just joining a read that
 * started before the event this is refreshing for (e.g. a purchase): the very
 * next `sharedEntitlements()` call, from whichever mounted hook's effect runs
 * first, starts a brand new GET, and every other mounted hook's effect (which
 * all fire in the same render pass, since `listeners` is a synchronous
 * `forEach`) joins THAT one request through the ordinary coalescer -- so a
 * refresh notifying five mounted hooks still costs exactly one GET.
 *
 * Call this ONCE per event that actually changed the tenant's plan (a
 * confirmed purchase, an explicit Retry) -- never from a render, and never
 * from an effect keyed on `services`/`plan`/anything this hook itself sets,
 * or a hook's own re-read would immediately trigger another refresh and loop
 * forever. Nothing in this module re-reads on a timer or a response; the only
 * two callers are `BillingSuccessPage` (once, after the purchase is
 * CONFIRMED, never on a still-polling read) and this hook's own Retry paths.
 */
export function refreshEntitlements(): void {
  inFlightEntitlements = null;
  listeners.forEach((listener) => listener());
}

export function useEntitlements(): Entitlements {
  const [state, setState] = useState<Entitlements>(OPEN);
  const [generation, setGeneration] = useState(0);

  // Subscribe once, for the hook's whole lifetime -- not tied to `generation`,
  // or every refresh would tear down and rebuild this subscription for no
  // reason. `refreshEntitlements()` above only ever bumps `generation`; it
  // never touches `state` directly, so the actual re-read below is the only
  // path that can ever change what this hook reports.
  useEffect(() => subscribeEntitlementsRefresh(() => setGeneration((g) => g + 1)), []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => (prev.known || prev.failed ? OPEN : prev)); // loading, neutrally
    sharedEntitlements()
      .then((r) => {
        if (cancelled) return;
        if (!r.ok || r.plan_readable === false || !r.features) {
          setState(FAILED);
          return;
        }
        const features: Record<string, EntitlementFeature> = r.features;
        setState({
          known: true,
          failed: false,
          plan: r.plan ?? null,
          limits: r.limits ?? null,
          services: r.services ?? null,
          has: (feature) => features[feature]?.included ?? true,
          lockFor: (feature) => {
            const entry = features[feature];
            if (!entry || entry.included) return null;
            const upgrade = entry.included_from;
            const upgradeLabel = upgrade ? PLAN_LABELS[upgrade] ?? upgrade : null;
            return {
              label: entry.label,
              includedFrom: upgrade,
              message: upgradeLabel
                ? `${entry.label} is included from ${upgradeLabel}.`
                : `${entry.label} is not included in your plan.`,
            };
          },
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState(FAILED);
      });
    return () => { cancelled = true; };
    // `generation` is a trigger only, not a value read inside the effect --
    // `sharedEntitlements()` reads no argument, so re-running this effect is
    // the entire point of listing it here.
  }, [generation]);

  return state;
}

/** Re-runs every mounted `useEntitlements()` (via `refreshEntitlements()`)
 *  and returns a promise for the SPECIFIC read this call kicked off, for a
 *  caller that wants to know when ITS refresh settles (e.g. a Retry button
 *  wants to stop showing "Retrying…" once this attempt is done) without
 *  subscribing to the broadcast itself. Exported separately from
 *  `refreshEntitlements()` because most callers (BillingSuccessPage) do not
 *  need the promise and the broadcast is enough. */
export function refreshEntitlementsAndWait(): Promise<EntitlementsResponse> {
  refreshEntitlements();
  return sharedEntitlements();
}
